import { inject, Injectable } from '@angular/core';
import { iif, Observable, of, Subject } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import { CandidateQuestionDataService } from '../database/services/candidate-question-data.service';
import { KeyValueDataService } from '../database/services/key-value-data.service';
import { QuestionSectionDataService } from '../database/services/question-section-data.service';
import { AnsweredQuestion } from '../model/exam/answered-question';
import { CandidateQuestionDto } from '../model/exam/candidate-question-dto';
import { CandidateExamStateDto } from '../model/exam/state/candidate-exam-state-dto';
import { QuestionSectionStateDto } from '../model/exam/state/question-section-state-dto';
import { QuestionStateDto } from '../model/exam/state/question-state-dto';
import { APIResponse } from '../model/request/APIResponse';
import { ConnectionService } from './connection.service';
import { ExamService } from './exam.service';
import { QuestionAccess, QuestionAccessService } from './question-access.service';

@Injectable({
	providedIn: 'root'
})
export class ExamNavigationService {

	public examState: CandidateExamStateDto = new CandidateExamStateDto();
	public currentQuestionIndex: number = 0;

	private currentQuestionSource: Subject<QuestionStateDto> = new Subject<QuestionStateDto>();
	public currentQuestionChange$ = this.currentQuestionSource.asObservable();

	private flaggedQuestionsSource: Subject<number[]> = new Subject<number[]>();
	public flaggedQuestionsChange$ = this.flaggedQuestionsSource.asObservable();

	private answeredQuestionsSource: Subject<AnsweredQuestion> = new Subject<AnsweredQuestion>();
	public answeredQuestionsChange$ = this.answeredQuestionsSource.asObservable();

	private readonly keyValueDataService: KeyValueDataService = inject(KeyValueDataService);
	private readonly candidateQuestionDataService: CandidateQuestionDataService = inject(CandidateQuestionDataService);
	private readonly questionSectionDataService: QuestionSectionDataService = inject(QuestionSectionDataService);
	private readonly connectionService: ConnectionService = inject(ConnectionService);
	private readonly examService: ExamService = inject(ExamService);
	private readonly questionAccessService: QuestionAccessService = inject(QuestionAccessService);

	public getSubjectName(): Observable<string> {
		return this.keyValueDataService.getSubjectName();
	}

	public getScheduledFinish(): Observable<Date> {
		return this.keyValueDataService.getScheduledFinish();
	}

	public getCurrentQuestion(): Observable<QuestionStateDto> {
		return this.candidateQuestionDataService.getCurrentQuestionState();
	}

	public goToQuestion(questionNumber: number): Observable<QuestionAccess> {
		return this.candidateQuestionDataService.getQuestionByOrderIndex(questionNumber)
			.pipe(mergeMap((candidateQuestionDto?: CandidateQuestionDto) => this.questionAccessService.resolve(candidateQuestionDto)));
	}

	public goToNextQuestion(): Observable<QuestionAccess> {
		return this.candidateQuestionDataService.getCurrentQuestionState()
			.pipe(mergeMap((questionState: QuestionStateDto) => this.candidateQuestionDataService.getQuestionByOrderIndex(questionState.question.orderIndex + 1)))
			.pipe(mergeMap((candidateQuestionDto?: CandidateQuestionDto) => this.questionAccessService.resolve(candidateQuestionDto)));
	}

	public goToPreviousQuestion(): Observable<QuestionAccess> {
		return this.candidateQuestionDataService.getCurrentQuestionState()
			.pipe(mergeMap((questionState: QuestionStateDto) => this.candidateQuestionDataService.getQuestionByOrderIndex(questionState.question.orderIndex - 1)))
			.pipe(mergeMap((candidateQuestionDto?: CandidateQuestionDto) => this.questionAccessService.resolve(candidateQuestionDto)));
	}

	public getFirstReachableSectionNumber(): Observable<number> {
		return this.questionSectionDataService.getFirstReachableSectionNumber();
	}

	public getExcludeSectionDuration(): Observable<boolean> {
		return this.keyValueDataService.getExcludeSectionDuration();
	}

	public getCanRevisitSections(): Observable<boolean> {
		return this.questionSectionDataService.getCanRevisitSections();
	}

	public getQuestionCount(): Observable<CandidateQuestionDto[]> {
		return this.candidateQuestionDataService.getAllQuestions();
	}

	public getQuestionSectionsWithCandidateQuestions(): Observable<QuestionSectionStateDto[]> {
		return this.questionSectionDataService.getSectionQuestions();
	}

	public flagCurrentQuestion(): void {
		this.candidateQuestionDataService.flagCurrentQuestion()
			.pipe(mergeMap(() => this.getFlaggedQuestionNumbers()))
			.pipe(map((flaggedNumbers: number[]) => this.flaggedQuestionsSource.next(flaggedNumbers)))
			.pipe(first())
			.subscribe();
	}

	public setAnsweredCurrentQuestion(question: AnsweredQuestion): void {
		this.answeredQuestionsSource.next(question);
	}

	public getFlaggedQuestionNumbers(): Observable<number[]> {
		return this.candidateQuestionDataService.getFlaggedQuestionNumbers();
	}


	public getLastQuestion(): Observable<QuestionStateDto> {
		return this.candidateQuestionDataService.getLastQuestionState();
	}

	public updateCurrentQuestion(question: QuestionStateDto): Observable<APIResponse<void>> {
		const newQuestionIndex: number = question.question.orderIndex;

		return this.keyValueDataService.setCurrentQuestionNumber(question.question.orderIndex)
			.pipe(map(() => this.currentQuestionSource.next(question)))
			.pipe(mergeMap(() => iif(() => this.connectionService.isOnline(),
										this.examService.updateCurrentQuestionNumber(newQuestionIndex),
										of())));
	}
}
